<script setup lang="ts">
import { onMounted, ref, type Ref } from 'vue'
import AppModal from '@/components/ui/AppModal.vue'
import router from '@/router'
import type { AxiosResponse } from 'axios'
import Pagination from '@/commons/components/Pagination.vue'
import {useInstitutionStore} from "@/domains/home/services/nir/associate-registers/stores";
import {useFormsStore} from "@/domains/home/services/nir/configurations/stores/forms.ts";
import {Form} from "@/domains/home/services/nir/configurations/types/forms.ts";
import api from "@/app/services/apiNir.ts";
import {useNotificationsStore} from "@/app/notify/notifications.ts";

type CreateForm = {
  name: string
  description: string
  entityTypes : {
    entityTypeId: string
    subEntityTypeIds: string[]
  }[]
  entityId: string
  purpose: string
}

type DuplicateForm = {
  name: string
  formId: string
  entityId: string
  entityTypeId: string
  subEntityTypeId: string
  description: string
  purpose: string
}

const purposeOptions = [
  { value: 'REGISTRATION', label: 'Registration' },
  { value: 'INCOMPLETE_DATA', label: 'Incomplete Data' },
  { value: 'AMENDMENT', label: 'Amendment' },
]

const form = ref<CreateForm>({
  name: '',
  description: '',
  entityTypes:[],
  entityId: '',
  purpose: '',
})

const duplicateForm = ref<DuplicateForm>({
  name: '',
  description: '',
  formId: '',
  entityId: '',
  entityTypeId: '',
  subEntityTypeId: '',
  purpose: '',
})

const showCopyFormModal = ref(false)
const duplicateEntityTypes = ref<any[]>([])
const duplicateSubEntityTypes = ref<any[]>([])

const createFormModalOpen = ref(false)
const institutionFilter = ref('')
const purposeFilter = ref('')
const nameFilter = ref('')
const loading = ref(false)
const store = useFormsStore()
const institutionStore = useInstitutionStore()
const notify = useNotificationsStore()
const page = ref(1)
const limit = ref(16)

onMounted(() => {
  institutionStore.fetchInstitutions(1, 1000)
  store.fetchEntityTypes('')
  store.fetchPrimaryEntityTypes()
  fetch()
})

function fetch() {
  loading.value = true
  store.fetchForms(nameFilter.value, institutionFilter.value, purposeFilter.value, page.value, limit.value).then(() => {
    loading.value = false
  })
}

// Track sub-entity types for each entity type entry
const entityTypeSubTypes = ref<Record<string, any[]>>({})

function fetchTypes(){
  if(form.value.entityId){
    console.log(form.value.entityId)
    store.fetchPrimaryInstitutionEntityTypes(form.value.entityId)
  }
  if(duplicateForm.value.entityId){
    console.log(duplicateForm.value.entityId)
    store.fetchPrimaryInstitutionEntityTypes(duplicateForm.value.entityId)
  }

  // Reset entity types array when institution changes
  form.value.entityTypes = []
  entityTypeSubTypes.value = {}
  duplicateForm.value.entityTypeId = ''
  duplicateForm.value.subEntityTypeId = ''
}

function addEntityTypeEntry() {
  form.value.entityTypes.push({
    entityTypeId: '',
    subEntityTypeIds: []
  })
}

function removeEntityTypeEntry(index: number) {
  const entry = form.value.entityTypes[index]
  if (entry.entityTypeId) {
    delete entityTypeSubTypes.value[entry.entityTypeId]
  }
  form.value.entityTypes.splice(index, 1)
}

function onEntityTypeChange(index: number) {
  const entry = form.value.entityTypes[index]
  entry.subEntityTypeIds = []

  if (entry.entityTypeId) {
    // Fetch sub-entity types for this entity type
    store.fetchSubEntityTypes(entry.entityTypeId).then(() => {
      entityTypeSubTypes.value[entry.entityTypeId] = store.subEntityTypes || []
    })
  }
}

function toggleSubEntityType(entryIndex: number, subTypeId: string) {
  const entry = form.value.entityTypes[entryIndex]
  const idx = entry.subEntityTypeIds.indexOf(subTypeId)
  if (idx > -1) {
    entry.subEntityTypeIds.splice(idx, 1)
  } else {
    entry.subEntityTypeIds.push(subTypeId)
  }
}

function isSubEntityTypeSelected(entryIndex: number, subTypeId: string) {
  return form.value.entityTypes[entryIndex].subEntityTypeIds.includes(subTypeId)
}

function getEntityTypeName(entityTypeId: string) {
  return store.institutionEntityTypes?.find(t => t.id === entityTypeId)?.name || ''
}

// Get available entity types (exclude already selected ones)
function getAvailableEntityTypes(currentEntryIndex: number) {
  const selectedIds = form.value.entityTypes
    .filter((_, idx) => idx !== currentEntryIndex)
    .map(e => e.entityTypeId)
    .filter(id => id !== '')

  return store.institutionEntityTypes?.filter(type => !selectedIds.includes(type.id)) || []
}

// Check if can add more entity types
function canAddMoreEntityTypes() {
  const totalAvailable = store.institutionEntityTypes?.length || 0
  const currentSelected = form.value.entityTypes.length
  return form.value.entityId && currentSelected < totalAvailable
}

function createForm() {
  loading.value = true
  store.createForm(form.value).then(() => {
    loading.value = false
    createFormModalOpen.value = false
    fetch()
  })
}

function copyForm() {
  loading.value = true
  store.copyForm(duplicateForm.value).then(() => {
    loading.value = false
    showCopyFormModal.value = false
    resetDuplicateForm()
    fetch()
  })
}

function editForm(f: Form) {
  console.log('Edit form:', f)
}

function viewForm(id: string) {
  router.push({ name: 'nir-forms-client', params: { id: id } })
}

function editFormData(f: Form) {
  console.log('Edit formData:', f.id)
  router.push({ name: 'nir-forms-data', params: { id: f.id } })
}

function copy(f: Form) {
  duplicateForm.value = {
    name: '',
    formId: f.id,
    entityId: f.entityId,
    entityTypeId: f.entityTypeId,
    subEntityTypeId: f.subEntityTypeId,
    description: f.description,
    purpose: '',
  }
  fetchDuplicateEntityTypes()
  fetchDuplicateSubEntityTypes()
  showCopyFormModal.value = true
}

function fetchDuplicateEntityTypes() {
  store.fetchPrimaryInstitutionEntityTypes(duplicateForm.value.entityId).then(() => {
    duplicateEntityTypes.value = store.institutionEntityTypes || []
  })
}

function fetchDuplicateSubEntityTypes() {
  store.fetchSubEntityTypes(duplicateForm.value.entityTypeId).then(() => {
    duplicateSubEntityTypes.value = store.subEntityTypes || []
  })
}

function onDuplicateEntityTypeChange() {
  duplicateForm.value.subEntityTypeId = ''
  if (duplicateForm.value.entityTypeId) {
    store.fetchSubEntityTypes(duplicateForm.value.entityTypeId).then(() => {
      duplicateSubEntityTypes.value = store.subEntityTypes || []
    })
  }
}

function resetDuplicateForm() {
  duplicateForm.value = {
    name: '',
    description: '',
    formId: '',
    entityId: '',
    entityTypeId: '',
    subEntityTypeId: '',
    purpose: '',
  }
  duplicateEntityTypes.value = []
  duplicateSubEntityTypes.value = []
}

function cancelCopy() {
  showCopyFormModal.value = false
  resetDuplicateForm()
}

function getStatusColor(status: string) {
  return status == 'PUBLISHED'
    ? 'text-green-600 bg-green-100'
    : status == 'APPROVED'
      ? 'text-green-600 bg-green-100'
      : 'text-gray-600 bg-gray-100'
}

function resetForm() {
  form.value = {
    name: '',
    description: '',
    entityTypes: [],
    entityId: '',
    purpose: '',
  }
  entityTypeSubTypes.value = {}
}

function downloadTemplate(id: string) {
  download(id)
}

const download = async (id: string) => {
  const path = `/v1/configurations/forms/template?formId=${id}`

  try {
    const response: AxiosResponse<Blob> = await api.get(path, {
      responseType: 'blob',
    })

    // Create a blob from the response
    const blob = new Blob([response.data], {
      type:
        response.headers['content-type'] ||
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    })

    // Create a temporary URL for the blob
    const url = window.URL.createObjectURL(blob)

    // Create a temporary anchor element to trigger download
    const link = document.createElement('a')
    link.href = url

    // Extract filename from Content-Disposition header if available
    const contentDisposition = response.headers['content-disposition']
    let filename = 'form-template.xlsx'

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '')
      }
    }

    // Use form name if available
    if (store.form?.name) {
      filename = `${store.form.name}-template.xlsx`
    }

    link.download = filename

    // Append to body, click, and remove
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    // Clean up the blob URL
    window.URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Error downloading template:', error)
    // You could add a toast notification here to inform the user of the error
  }
}

function update(pg: number) {
  page.value = pg
  fetch()
}

function cancel() {
  createFormModalOpen.value = false
  resetForm()
}

function copyCode(code: string) {
  navigator.clipboard.writeText(code).then(() => {
    notify.success('Copied to clipboard')
  })
}
</script>

<template>
  <div class="flex-1 flex flex-col">
    <!-- Action Bar -->
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center space-x-3">
        <div class="relative">
          <input
            type="text"
            v-model="nameFilter"
            @change="fetch"
            placeholder="Search forms..."
            class="pl-10 pr-4 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary w-64"
          />
        </div>

        <div>
          <select
            v-model="institutionFilter"
            @change="fetch"
            required
            class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
          >
            <option value="">Select Institution</option>
            <option
              v-for="(type, idx) in institutionStore.institutions"
              :key="idx"
              :value="type.id"
            >
              {{ type.name }}
            </option>
          </select>
        </div>

        <div>
          <select
              v-model="purposeFilter"
              @change="fetch"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
          >
            <option value="">Select Purpose</option>
            <option value="REGISTRATION">New Registration</option>
            <option value="INCOMPLETE_DATA">Data Update (Incomplete Data)</option>
            <option value="AMENDMENT">Amendment</option>
          </select>
        </div>
      </div>

      <button class="btn-primary"
        @click="createFormModalOpen = true">
        <i class="fa-solid fa-plus"></i>
        <span>Create Form</span>
      </button>
    </div>

    <!-- Forms Table -->
    <div class="bg-white shadow flex flex-col">
      <div class="overflow-x-auto flex-1">
        <table class="w-full text-xs">
          <thead class="bg-gray-50 sticky top-0">
            <tr>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
                #
              </th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
                Associate Register
              </th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
                Form Name / Title
              </th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
                Purpose
              </th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
                Type
              </th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
                Sub Type
              </th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
                Status
              </th>
<!--              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">-->
<!--                Direct Admission-->
<!--              </th>-->
<!--              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">-->
<!--                Interim ERN-->
<!--              </th>-->
              <th
                class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
                Publish
              </th>
              <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">
                Form Code
              </th>
              <th
                class="text-center py-3 px-4 font-semibold text-gray-700 border-b border-gray-200"
              ></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, idx) in store.forms" :key="idx" class="hover:bg-gray-50 transition">
              <td class="py-2 px-4 border-b border-gray-200">
                <span>{{(idx + (page - 1) * limit) + 1}}</span>
              </td>
              <td class="py-2 px-4 border-b border-gray-200">
                <span>
                  {{ institutionStore.institutions?.find((i) => i.id === item.entityId)?.name }}
                </span>
              </td>
              <td class="py-2 px-4 border-b border-gray-200 font-bold">
                <span>{{ item.name }}</span>
              </td>
              <td class="py-2 px-4 border-b border-gray-200">
                <span class="capitalize">{{ item.purpose.toLowerCase().replace(/_/g, ' ') }}</span>
              </td>
              <td class="py-2 px-4 border-b border-gray-200">
                {{ store.entityTypes?.find((i) => i.id === item.entityTypeId)?.name || '--' }}
              </td>
              <td class="py-2 px-4 border-b border-gray-200">
                {{ store.entityTypes?.find((i) => i.id === item.subEntityTypeId)?.name || '--' }}
              </td>

              <td class="py-2 px-4 border-b border-gray-200">
                <span class="px-3 py-1 rounded-lg" :class="getStatusColor(item.status)">
                  {{ item.status }}
                </span>
              </td>
<!--              <td class="py-3 px-4 border-b border-gray-200">-->
<!--                    <span-->
<!--                        :class="[-->
<!--                        'px-2 py-1 text-xs rounded-full',-->
<!--                        item.hasDirectAdmission-->
<!--                          ? 'bg-green-100 text-green-800'-->
<!--                          : 'bg-gray-100 text-gray-500',-->
<!--                      ]"-->
<!--                    >-->
<!--                      <i-->
<!--                          :class="item.hasDirectAdmission ? 'fas fa-check-circle' : 'fas fa-ban'"-->
<!--                      ></i>-->
<!--                      {{ item.hasDirectAdmission ? 'Enabled' : 'Disabled' }}-->
<!--                    </span>-->
<!--              </td>-->
<!--              <td class="py-3 px-4 border-b border-gray-200">-->
<!--                    <span-->
<!--                        :class="[-->
<!--                        'px-2 py-1 text-xs rounded-full',-->
<!--                        item.issuesInterimErn-->
<!--                          ? 'bg-green-100 text-green-800'-->
<!--                          : 'bg-gray-100 text-gray-500',-->
<!--                      ]"-->
<!--                    >-->
<!--                      <i :class="item.issuesInterimErn ? 'fas fa-check-circle' : 'fas fa-ban'"></i>-->
<!--                      {{ item.issuesInterimErn ? 'Enabled' : 'Disabled' }}-->
<!--                    </span>-->
<!--              </td>-->
              <td class="py-2 px-4 border-b border-gray-200">
                <span
                  v-if="item.publishedAt"
                  class="text-green-600 bg-green-100 px-3 py-1 rounded-xl">
                  <i class="fa-solid fa-check"></i> Published
                </span>
                <span v-else class="text-gray-600 bg-gray-100 px-3 py-1 rounded-xl">
                  <i class="fa-solid fa-clock"></i> Waiting
                </span>
              </td>
              <td class="py-2 px-4 border-b border-gray-200">
                <span class="bg-primary-100 rounded-lg text-primary p-1">{{ item.code }} <i class="fa-regular fa-copy" @click="copyCode(item.code)"></i></span>
              </td>
              <td class="py-2 px-4 border-b border-gray-200">
                <div class="flex gap-2 items-center">
                  <button class="btn-sm-primary"
                    @click="viewForm(item.id)"
                    title="Edit">
                    <i class="fa-solid fa-eye  mx-auto"></i>
                  </button>
<!--                  <button class="btn-sm-primary"-->
<!--                    @click="downloadTemplate(item.id)">-->
<!--                    <i class="fa-solid fa-download  mx-auto"></i>-->
<!--                  </button>-->
                  <button class="btn-sm-primary"
                          v-if="!item.publishedAt"
                    @click="editFormData(item)"
                    title="Edit">
                    <i class="fa-solid fa-pen mx-auto"></i>
                  </button>
                  <button class="btn-sm-primary"
                          v-if="item.publishedAt"
                          @click="copy(item)"
                          title="Copy">
                    <i class="fa-solid fa-copy mx-auto"></i>
                  </button>
                </div>
              </td>
            </tr>
            <!-- Empty State -->
            <tr v-if="!store.forms || store.forms.length === 0">
              <td colspan="9" class="py-12 text-center">
                <div class="flex flex-col items-center justify-center text-gray-500">
                  <i class="fa-solid fa-file-alt text-4xl mb-3 text-gray-300"></i>
                  <p class="text-lg font-medium">No forms found</p>
                  <p class="text-sm">Create a new form to get started</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <Pagination
        :pagination="store.pagination"
        :page="page"
        :limit="limit"
        label="forms"
        @update="update"
      />
    </div>

    <!-- Create Form Modal -->
    <AppModal v-model="createFormModalOpen" xl4>
      <div class="p-2">
        <h2 class="text-lg font-bold text-gray-900 mb-4">Create New Form</h2>

        <form @submit.prevent="createForm" class="space-y-4">
          <div class="grid grid-cols-2 gap-4">
            <!-- Form Name -->
            <div class="col-span-2">
              <label class="block text-sm font-medium text-gray-700 mb-1.5">
                <i class="fa-solid fa-file-alt text-gray-400 mr-1"></i>
                Form Name *
              </label>
              <input
                v-model="form.name"
                type="text"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                placeholder="e.g., Limited Company Registration Form"
              />
            </div>

            <!-- Institution -->
            <div class="col-span-2">
              <label class="block text-sm font-medium text-gray-700 mb-1.5">
                <i class="fa-solid fa-landmark text-gray-400 mr-1"></i>
                Institution *
              </label>
              <select
                v-model="form.entityId"
                @change="fetchTypes"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="">Select Institution</option>
                <option
                  v-for="institution in institutionStore.institutions"
                  :key="institution.id"
                  :value="institution.id"
                >
                  {{ institution.name }} ({{ institution.code }})
                </option>
              </select>
            </div>

            <!-- Purpose -->
            <div class="col-span-2">
              <label class="block text-sm font-medium text-gray-700 mb-1.5">
                <i class="fa-solid fa-bullseye text-gray-400 mr-1"></i>
                Purpose *
              </label>
              <select
                v-model="form.purpose"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="">Select Purpose</option>
                <option
                  v-for="option in purposeOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>

            <!-- Entity Types Multi-Selector -->
            <div class="col-span-2">
              <div class="flex items-center justify-between mb-2">
                <label class="block text-sm font-medium text-gray-700">
                  <i class="fa-solid fa-layer-group text-gray-400 mr-1"></i>
                  Entity Types & Sub Types *
                  <span v-if="form.entityTypes.length > 0" class="ml-2 bg-primary-100 text-primary px-2 py-0.5 rounded-full text-xs">
                    {{ form.entityTypes.length }} added
                  </span>
                </label>
                <button
                  type="button"
                  @click="addEntityTypeEntry"
                  :disabled="!form.entityId || !canAddMoreEntityTypes()"
                  class="btn-sm-primary"
                >
                  <i class="fa-solid fa-plus mr-1"></i> Add Entity Type
                </button>
              </div>

              <!-- Entity Type Entries -->
              <div v-if="form.entityTypes.length > 0" class="space-y-3 max-h-80 overflow-y-auto">
                <div
                  v-for="(entry, entryIndex) in form.entityTypes"
                  :key="entryIndex"
                  class="border border-gray-200 rounded-lg p-3 bg-gray-50"
                >
                  <div class="flex items-center justify-between mb-2">
                    <span class="text-xs font-semibold text-gray-500 bg-gray-200 px-2 py-1 rounded">
                      #{{ entryIndex + 1 }}
                    </span>
                    <!-- Remove Button -->
                    <button
                      type="button"
                      @click="removeEntityTypeEntry(entryIndex)"
                      class="btn-sm-danger"
                      title="Remove this entity type"
                    >
                      <i class="fa-solid fa-trash"></i>
                    </button>
                  </div>

                  <div class="flex items-start gap-3">
                    <!-- Entity Type Dropdown -->
                    <div class="flex-1">
                      <label class="block text-xs font-medium text-gray-600 mb-1">
                        Entity Type
                      </label>
                      <select
                        v-model="entry.entityTypeId"
                        @change="onEntityTypeChange(entryIndex)"
                        required
                        class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                      >
                        <option value="">Select Entity Type</option>
                        <option
                          v-for="type in getAvailableEntityTypes(entryIndex)"
                          :key="type.id"
                          :value="type.id"
                        >
                          {{ type.name }}
                        </option>
                        <!-- Keep current selection visible even if it's "taken" -->
                        <option
                          v-if="entry.entityTypeId && !getAvailableEntityTypes(entryIndex).find(t => t.id === entry.entityTypeId)"
                          :value="entry.entityTypeId"
                        >
                          {{ getEntityTypeName(entry.entityTypeId) }}
                        </option>
                      </select>
                    </div>
                  </div>

                  <!-- Sub Entity Types Multi-Select -->
                  <div v-if="entry.entityTypeId && entityTypeSubTypes[entry.entityTypeId]?.length > 0" class="mt-3">
                    <div class="flex items-center justify-between mb-2">
                      <label class="block text-xs font-medium text-gray-600">
                        Sub Entity Types
                        <span class="text-primary ml-1">({{ entry.subEntityTypeIds.length }} / {{ entityTypeSubTypes[entry.entityTypeId]?.length }} selected)</span>
                      </label>
                      <button
                        type="button"
                        @click="entry.subEntityTypeIds = entityTypeSubTypes[entry.entityTypeId].map((s: any) => s.id)"
                        class="text-xs text-primary hover:underline"
                      >
                        Select All
                      </button>
                    </div>
                    <div class="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto border border-gray-200 rounded p-2 bg-white">
                      <div
                        v-for="subType in entityTypeSubTypes[entry.entityTypeId]"
                        :key="subType.id"
                        class="flex items-center"
                      >
                        <input
                          type="checkbox"
                          :id="`subtype-${entryIndex}-${subType.id}`"
                          :checked="isSubEntityTypeSelected(entryIndex, subType.id)"
                          @change="toggleSubEntityType(entryIndex, subType.id)"
                          class="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                        />
                        <label
                          :for="`subtype-${entryIndex}-${subType.id}`"
                          class="ml-2 text-sm text-gray-700 cursor-pointer"
                        >
                          {{ subType.name }}
                        </label>
                      </div>
                    </div>
                  </div>

                  <!-- Loading state for sub types -->
                  <div v-else-if="entry.entityTypeId && !entityTypeSubTypes[entry.entityTypeId]" class="mt-3 text-sm text-gray-500">
                    <i class="fa-solid fa-spinner fa-spin mr-1"></i> Loading sub-entity types...
                  </div>

                  <!-- No sub types message -->
                  <div v-else-if="entry.entityTypeId && entityTypeSubTypes[entry.entityTypeId]?.length === 0" class="mt-3 text-sm text-amber-600 bg-amber-50 p-2 rounded">
                    <i class="fa-solid fa-exclamation-triangle mr-1"></i> No sub-entity types available for this entity type.
                  </div>
                </div>
              </div>

              <!-- Empty State -->
              <div v-else class="border border-dashed border-gray-300 rounded-lg p-6 text-center text-gray-500">
                <i class="fa-solid fa-layer-group text-2xl mb-2"></i>
                <p class="text-sm">No entity types added yet.</p>
                <p class="text-xs">Select an institution first, then click "Add Entity Type" to add mappings.</p>
              </div>

              <p class="text-xs text-gray-500 mt-2">
                <i class="fa-solid fa-info-circle mr-1"></i>
                Forms will be created for each combination of entity type and its selected sub-entity types.
                <span v-if="store.institutionEntityTypes?.length" class="text-primary">
                  ({{ store.institutionEntityTypes.length }} entity types available)
                </span>
              </p>
            </div>

            <!-- Description -->
            <div class="col-span-2">
              <label class="block text-sm font-medium text-gray-700 mb-1.5">
                <i class="fa-solid fa-align-left text-gray-400 mr-1"></i>
                Description *
              </label>
              <textarea
                v-model="form.description"
                required
                rows="2"
                class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                placeholder="Brief description of the form"
              ></textarea>
            </div>
          </div>

          <!-- Form Actions -->
          <div class="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              @click="cancel"
              class="btn-danger"
            >
              <i class="fas fa-times mr-1"></i>
              Cancel
            </button>
            <button
              type="submit"
              :disabled="loading"
              class="btn-primary px-4 py-2 bg-primary hover:bg-primary-700 text-white rounded text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i class="fa-solid fa-check mr-1"></i>
              <span v-if="!loading">Create Form</span>
              <span v-else>Creating...</span>
            </button>
          </div>
        </form>
      </div>
    </AppModal>

    <!-- Copy Form Modal -->
    <AppModal v-model="showCopyFormModal" xl2>
      <div class="p-2">
        <h2 class="text-lg font-bold text-gray-900 mb-4">
          <i class="fa-solid fa-copy text-primary mr-2"></i>
          Duplicate Form
        </h2>

        <form @submit.prevent="copyForm" class="space-y-4">
          <div class="grid grid-cols-2 gap-4">
            <!-- New Form Name -->
            <div class="col-span-2">
              <label class="block text-sm font-medium text-gray-700 mb-1.5">
                <i class="fa-solid fa-file-alt text-gray-400 mr-1"></i>
                New Form Name *
              </label>
              <input
                v-model="duplicateForm.name"
                type="text"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                placeholder="e.g., Limited Company Registration Form (Copy)"
              />
            </div>


            <div class="col-span-2">
              <label class="block text-sm font-medium text-gray-700 mb-1.5">
                <i class="fa-solid fa-landmark text-gray-400 mr-1"></i>
                Institution *
              </label>
              <select
                  v-model="duplicateForm.entityId"
                  @change="fetchTypes"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="">Select Institution</option>
                <option
                    v-for="institution in institutionStore.institutions"
                    :key="institution.id"
                    :value="institution.id">
                  {{ institution.name }} ({{ institution.code }})
                </option>
              </select>
            </div>

            <!-- Purpose -->
            <div class="col-span-2">
              <label class="block text-sm font-medium text-gray-700 mb-1.5">
                <i class="fa-solid fa-bullseye text-gray-400 mr-1"></i>
                Purpose *
              </label>
              <select
                v-model="duplicateForm.purpose"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="">Select Purpose</option>
                <option
                  v-for="option in purposeOptions"
                  :key="option.value"
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>

            <!-- Entity Type -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1.5">
                <i class="fa-solid fa-building text-gray-400 mr-1"></i>
                Entity Type *
              </label>
              <select
                v-model="duplicateForm.entityTypeId"
                @change="onDuplicateEntityTypeChange"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="">Select Entity Type</option>
                <option v-for="(type, idx) in store.institutionEntityTypes" :key="idx" :value="type.id">
                  {{ type.name }}
                </option>
              </select>
            </div>

            <!-- Sub Entity Type -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1.5">
                <i class="fa-solid fa-building text-gray-400 mr-1"></i>
                Sub Entity Type
              </label>
              <select
                v-model="duplicateForm.subEntityTypeId"
                class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="">Select Sub Entity Type</option>
                <option v-for="(type, idx) in store.subEntityTypes" :key="idx" :value="type.id">
                  {{ type.name }}
                </option>
              </select>
            </div>

            <div class="col-span-2">
              <label class="block text-sm font-medium text-gray-700 mb-1.5">
                <i class="fa-solid fa-align-left text-gray-400 mr-1"></i>
                Description *
              </label>
              <textarea
                  v-model="duplicateForm.description"
                  required
                  rows="2"
                  class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="Brief description of the form"
              ></textarea>
            </div>
          </div>

          <!-- Form Actions -->
          <div class="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              @click="cancelCopy"
              class="btn-danger"
            >
              <i class="fas fa-times mr-1"></i>
              Cancel
            </button>
            <button
              type="submit"
              :disabled="loading || !duplicateForm.name"
              class="btn-primary px-4 py-2 bg-primary hover:bg-primary-700 text-white rounded text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i class="fa-solid fa-copy mr-1"></i>
              <span v-if="!loading">Duplicate Form</span>
              <span v-else>Duplicating...</span>
            </button>
          </div>
        </form>
      </div>
    </AppModal>
  </div>
</template>

<style scoped>
table {
  border-collapse: separate;
  border-spacing: 0;
}
</style>
